/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PreferencesFinancial.scss';

import { bool } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as FinancialIcon } from 'assets/icons/coin.svg';
import FeatureLink from 'components/layouts/Navigation/FeatureLink/FeatureLink';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesSectionHeader from 'components/layouts/Preferences/shared/PreferencesSectionHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import { linkBuilder, preferencesLinks } from 'services/Externals';
import {
  canViewAccountRegistration,
  canViewAutomaticTransactions,
  canViewDividendsCapGains,
  canViewCheckwriting,
  canViewCostBasisMethod,
  hasCollegeSavingsOnly,
  getView,
  hasActiveAdvisoryOnly
} from 'state/view/ViewSelectors';
import { canViewBankingPage } from 'state/linkedBankAccounts/LinkedBankAccountsSelectors';

const DesktopFinancial = ({
  canViewAccountRegistration,
  canViewAutomaticTransactions,
  canViewDividendsCapGains,
  canViewCheckwriting,
  canViewCostBasisMethod,
  canViewBankingPage,
  hasCollegeSavingsOnly,
  hasActiveAdvisoryOnly,
  view
}) => (
  <Fragment>
    {canViewBankingPage && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Bank Accounts" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-financial-text">
            Move your money quickly by linking your investment accounts to your
            bank accounts.
          </div>
        </PreferencesCol>
        <PreferencesCol col={3}>
          <Link
            className="preferences-financial-link"
            id="e2e-profile-update-bank-accounts-link"
            to="/profile/linked-bank-accounts">
            <span>Add/Update Bank Accounts</span>
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewAccountRegistration && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Account Registration" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-financial-text">
            Review your account registration information and access the proper
            forms to update if needed.
          </div>
        </PreferencesCol>
        <PreferencesCol col={3}>
          <FeatureLink
            className="preferences-financial-link"
            id="e2e-profile-account-registration"
            path="profile/account-registration">
            <span>Review Account Registration</span>
          </FeatureLink>
        </PreferencesCol>
      </PreferencesRow>
    )}

    <PreferencesRow>
      <PreferencesCol col={3}>
        <PreferencesRowLabel label="Other Settings" />
      </PreferencesCol>

      <PreferencesCol col={6}>
        {canViewAutomaticTransactions && (
          <div className="other-settings-link">
            <a
              className="settings-link"
              id="e2e-profile-automatic-transactions-link"
              href={linkBuilder(
                preferencesLinks.automaticTransactionsLink,
                view
              )}>
              {hasCollegeSavingsOnly
                ? 'Automatic Monthly Contribution'
                : 'Automatic Transactions'}
            </a>
            {hasCollegeSavingsOnly ? (
              <span className="settings-description">
                Set-up and manage recurring contributions
              </span>
            ) : (
              <span className="settings-description">
                Establish regular investments, transfers and withdrawals to stay
                on track for your goals.
              </span>
            )}
          </div>
        )}
        {canViewCheckwriting && (
          <div className="other-settings-link">
            <Link
              className="settings-link"
              id="e2e-profile-checkwriting-link"
              to="/profile/checkwriting">
              Checkwriting
            </Link>
            <span className="settings-description">
              View accounts eligible for checkwriting privileges and activate.
            </span>
          </div>
        )}
        {canViewCostBasisMethod && !hasActiveAdvisoryOnly && (
          <div className="other-settings-link">
            <Link
              className="settings-link"
              id="e2e-profile-cost-basis-link"
              to="/profile/cost-basis">
              Cost Basis Method
            </Link>
            <span className="settings-description">
              Review and update cost basis method on your account.
            </span>
          </div>
        )}
        <div className="other-settings-link">
          <Link
            className="settings-link"
            id="e2e-profile-customize-account-link"
            to="/profile/customize-account-view">
            Customize Account View
          </Link>
          <span className="settings-description">
            Set up your online investment experience the way you want to see it.
          </span>
        </div>
        {canViewDividendsCapGains && !hasActiveAdvisoryOnly && (
          <div className="other-settings-link">
            <Link
              className="settings-link"
              id="e2e-profile-div-cap-link"
              to="/profile/dividends-and-capital-gains">
              Dividends &amp; Capital Gains
            </Link>
            <span className="settings-description">
              Review and update your dividends and capital gains information.
            </span>
          </div>
        )}
      </PreferencesCol>

      <PreferencesCol col={3} />
    </PreferencesRow>
  </Fragment>
);

const MobileFinancial = ({
  canViewAccountRegistration,
  canViewAutomaticTransactions,
  canViewDividendsCapGains,
  canViewCheckwriting,
  canViewCostBasisMethod,
  canViewBankingPage,
  hasCollegeSavingsOnly,
  hasActiveAdvisoryOnly,
  view
}) => (
  <Fragment>
    {canViewAutomaticTransactions && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <a
            className="settings-link"
            href={linkBuilder(
              preferencesLinks.automaticTransactionsLink,
              view
            )}>
            <div>
              {hasCollegeSavingsOnly
                ? 'Automatic Monthly Contribution'
                : 'Automatic Transactions'}
            </div>
            <ChevronRight className="preferences-chevron-right" />
          </a>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewBankingPage && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <Link
            className="settings-link"
            id="e2e-profile-update-bank-accounts-link"
            to="/profile/linked-bank-accounts">
            <div>Bank Accounts</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewAccountRegistration && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <FeatureLink
            className="settings-link"
            id="e2e-profile-account-registration"
            path="profile/account-registration">
            <div>Review Account Registration</div>
            <ChevronRight className="preferences-chevron-right" />
          </FeatureLink>
        </PreferencesCol>
      </PreferencesRow>
    )}

    {canViewCheckwriting && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <Link className="settings-link" to="/profile/checkwriting">
            <div>Checkwriting</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewCostBasisMethod && !hasActiveAdvisoryOnly && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <Link className="settings-link" to="/profile/cost-basis">
            <div>Cost Basis Method</div>
            <ChevronRight className="preferences-chevron-right" />
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    <PreferencesRow>
      <PreferencesCol col={12}>
        <Link className="settings-link" to="/profile/customize-account-view">
          <div>Customize Account View</div>
          <ChevronRight className="preferences-chevron-right" />
        </Link>
      </PreferencesCol>
    </PreferencesRow>
    {canViewDividendsCapGains && !hasActiveAdvisoryOnly && (
      <PreferencesRow>
        <PreferencesCol col={12}>
          <span>
            <Link
              className="settings-link"
              id="e2e-profile-div-cap-link"
              to="/profile/dividends-and-capital-gains">
              <div>Dividends &amp; Capital Gains</div>
              <ChevronRight className="preferences-chevron-right" />
            </Link>
          </span>
        </PreferencesCol>
      </PreferencesRow>
    )}
  </Fragment>
);

const PreferencesFinancial = ({
  canViewAccountRegistration,
  canViewAutomaticTransactions,
  canViewDividendsCapGains,
  canViewCheckwriting,
  canViewCostBasisMethod,
  canViewBankingPage,
  isDesktopLayout,
  hasCollegeSavingsOnly,
  hasActiveAdvisoryOnly,
  view
}) => (
  <div className="preferences-financial">
    <PreferencesSectionHeader
      Icon={FinancialIcon}
      text="Financial Preferences"
    />
    {isDesktopLayout ? (
      <DesktopFinancial
        canViewAccountRegistration={canViewAccountRegistration}
        canViewAutomaticTransactions={canViewAutomaticTransactions}
        canViewDividendsCapGains={canViewDividendsCapGains}
        canViewCheckwriting={canViewCheckwriting}
        canViewCostBasisMethod={canViewCostBasisMethod}
        canViewBankingPage={canViewBankingPage}
        hasCollegeSavingsOnly={hasCollegeSavingsOnly}
        hasActiveAdvisoryOnly={hasActiveAdvisoryOnly}
        view={view}
      />
    ) : (
      <MobileFinancial
        canViewAccountRegistration={canViewAccountRegistration}
        canViewAutomaticTransactions={canViewAutomaticTransactions}
        canViewDividendsCapGains={canViewDividendsCapGains}
        canViewCheckwriting={canViewCheckwriting}
        canViewCostBasisMethod={canViewCostBasisMethod}
        canViewBankingPage={canViewBankingPage}
        hasCollegeSavingsOnly={hasCollegeSavingsOnly}
        hasActiveAdvisoryOnly={hasActiveAdvisoryOnly}
        view={view}
      />
    )}
  </div>
);

PreferencesFinancial.propTypes = {
  canViewAutomaticTransactions: bool.isRequired,
  canViewDividendsCapGains: bool.isRequired,
  canViewCheckwriting: bool.isRequired,
  canViewCostBasisMethod: bool.isRequired,
  canViewBankingPage: bool.isRequired
};

const mapStateToProps = (state) => ({
  canViewAccountRegistration: canViewAccountRegistration(state),
  canViewAutomaticTransactions: canViewAutomaticTransactions(state),
  canViewDividendsCapGains: canViewDividendsCapGains(state),
  canViewCheckwriting: canViewCheckwriting(state),
  canViewCostBasisMethod: canViewCostBasisMethod(state),
  canViewBankingPage: canViewBankingPage(state),
  isDesktopLayout: state.view.isDesktopLayout,
  view: getView(state),
  hasCollegeSavingsOnly: hasCollegeSavingsOnly(state),
  hasActiveAdvisoryOnly: hasActiveAdvisoryOnly(state)
});

export default connect(mapStateToProps)(PreferencesFinancial);
